import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { TIERS, MODULES, valueOfStack, api } from "../lib/brand";
import { captureAndGetUtm } from "../lib/utm";
import ExitIntentModal from "../components/ExitIntentModal";

const SectionLabel = ({ children }) => <span className="haven-label">{children}</span>;

export default function Join() {
  const location = useLocation();
  const initialTier = useMemo(() => {
    const sp = new URLSearchParams(location.search).get("tier");
    return TIERS.find((t) => t.key === sp)?.key || "annual";
  }, [location.search]);

  const [tierKey, setTierKey] = useState(initialTier);
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | loading | error
  const [errMsg, setErrMsg] = useState("");

  const tier = TIERS.find((t) => t.key === tierKey) || TIERS[1];

  const startCheckout = async () => {
    setErrMsg("");
    setState("loading");
    try {
      // Pull whichever attribution we can — UTMs from URL/session, document.referrer
      const utm = captureAndGetUtm();
      const { data } = await api.post("/checkout/session", {
        tier: tierKey,
        origin_url: window.location.origin,
        email: email || null,
        ...utm,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL returned.");
      }
    } catch (err) {
      setErrMsg(err?.response?.data?.detail || "Could not start checkout. Please try again.");
      setState("error");
    }
  };

  return (
    <main data-testid="join-page" className="haven-page-shell-wide" style={{ paddingTop: 32 }}>
      <ExitIntentModal source="join" />
      <div className="grid lg:grid-cols-12 gap-8">
        {/* LEFT: tier picker */}
        <div className="lg:col-span-7">
          <SectionLabel>Founding · 500 seats</SectionLabel>
          <h1
            className="font-serif mt-3"
            style={{ fontSize: "clamp(34px, 4.6vw, 54px)", lineHeight: 1.05, color: "var(--ink)" }}
          >
            Pick your founding rate.
            <br />
            <em style={{ color: "var(--gold)" }}>Lock it permanently.</em>
          </h1>
          <p className="mt-4" style={{ color: "var(--ink-soft)", fontSize: 15, lineHeight: 1.7, maxWidth: 580 }}>
            Whatever you pay today never goes up for you, as long as your subscription stays active. When the price
            moves to standard, your account stays on this rate.
          </p>

          <div className="grid md:grid-cols-3 gap-4 mt-7">
            {TIERS.map((t) => {
              const selected = tierKey === t.key;
              return (
                <button
                  key={t.key}
                  data-testid={`join-tier-${t.key}`}
                  type="button"
                  onClick={() => setTierKey(t.key)}
                  className="text-left haven-card"
                  style={{
                    padding: 22,
                    border: selected ? "2px solid var(--gold)" : "1px solid var(--border-soft)",
                    background: selected ? "var(--gold-faint)" : "var(--surface-card)",
                    boxShadow: selected ? "0 12px 30px -16px rgba(181,136,56,0.4)" : "var(--shadow-soft)",
                    cursor: "pointer",
                    position: "relative",
                  }}
                >
                  {t.highlight && (
                    <span
                      style={{
                        position: "absolute", top: -10, right: 18,
                        background: "var(--ink)", color: "var(--warm-white)",
                        padding: "3px 10px", borderRadius: 999,
                        fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase",
                      }}
                    >
                      Best value
                    </span>
                  )}
                  <div className="haven-label">{t.label}</div>
                  <div className="mt-2 flex items-baseline gap-1">
                    <span className="font-serif" style={{ fontSize: 38, color: "var(--ink)", lineHeight: 1 }}>
                      ${t.price}
                    </span>
                    <span style={{ color: "var(--ink-muted)", fontSize: 13 }}>{t.cadence}</span>
                  </div>
                  <div style={{ marginTop: 4, color: "var(--ink-muted)", fontSize: 12 }}>
                    <s>${t.standard}{t.cadence}</s>
                  </div>
                  <div style={{ marginTop: 8, color: "var(--gold)", fontSize: 12, fontWeight: 500 }}>
                    {t.savings}
                  </div>
                  <p style={{ marginTop: 12, color: "var(--ink-soft)", fontSize: 12.5, lineHeight: 1.5 }}>{t.pitch}</p>
                </button>
              );
            })}
          </div>

          <div className="haven-card mt-6" style={{ padding: 22 }}>
            <SectionLabel>You get all 16 modules</SectionLabel>
            <div className="mt-3 flex flex-wrap gap-2">
              {MODULES.map((m) => (
                <span key={m.name} className="haven-bubble" style={{ background: "var(--surface-card)", border: "1px solid var(--border-soft)", color: "var(--ink-soft)" }}>
                  {m.name}
                </span>
              ))}
            </div>
            <div className="mt-4" style={{ color: "var(--ink-muted)", fontSize: 12 }}>
              À la carte value: <b style={{ color: "var(--ink)" }}>${valueOfStack}+</b> · Bundled at founding from $29/mo
            </div>
          </div>
        </div>

        {/* RIGHT: sticky checkout panel */}
        <div className="lg:col-span-5">
          <div
            className="haven-card haven-card-gold"
            style={{ padding: 26, position: "sticky", top: 96 }}
            data-testid="join-checkout-panel"
          >
            <SectionLabel>Your seat</SectionLabel>
            <h3 className="font-serif mt-2" style={{ fontSize: 28, color: "var(--ink)", lineHeight: 1.1 }}>
              {tier.label}
            </h3>
            <div className="mt-3 flex items-baseline gap-1">
              <span className="font-serif" style={{ fontSize: 44, color: "var(--ink)", lineHeight: 1 }}>${tier.price}</span>
              <span style={{ color: "var(--ink-muted)", fontSize: 13 }}>{tier.cadence}</span>
            </div>
            <div style={{ marginTop: 6, color: "var(--ink-soft)", fontSize: 13 }}>
              <s>${tier.standard}{tier.cadence}</s> standard · <span style={{ color: "var(--gold)" }}>{tier.savings}</span>
            </div>

            <div className="haven-divider-grain" style={{ margin: "20px 0", background: "var(--gold-light)", opacity: 0.5 }} />

            <label htmlFor="join-email" style={{ fontSize: 12, color: "var(--ink-muted)", letterSpacing: "0.06em", textTransform: "uppercase" }}>
              Email (we lock the rate to this account)
            </label>
            <input
              id="join-email"
              data-testid="join-email-input"
              className="haven-input mt-2"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            <button
              data-testid="join-checkout-btn"
              onClick={startCheckout}
              className="haven-btn haven-btn-primary mt-4"
              style={{ width: "100%", fontSize: 14 }}
              disabled={state === "loading"}
            >
              {state === "loading" ? "Opening secure checkout…" : `Continue to Stripe — $${tier.price}${tier.cadence}`}
              <span aria-hidden="true">→</span>
            </button>
            {state === "error" && (
              <div data-testid="join-error" style={{ color: "var(--rose)", fontSize: 12, marginTop: 8 }}>
                {errMsg}
              </div>
            )}

            <ul className="mt-5 space-y-2" style={{ color: "var(--ink-soft)", fontSize: 12.5, lineHeight: 1.5 }}>
              <li>· Stripe-secured checkout</li>
              <li>· Founding rate locked permanently</li>
              <li>· Cancel anytime (monthly/annual) · Lifetime is yours forever</li>
              <li>· You'll be onboarded by Daniel personally</li>
            </ul>
          </div>
        </div>
      </div>
    </main>
  );
}
